import { useCallback, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import {
  Autocomplete,
  Box,
  Button,
  Description,
  Divider,
  FieldError,
  Flexbox,
  Heading,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Stack,
  StackRow,
  Textarea,
  shootToast,
} from "codekit";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useGetAutocompleteWithParamsMutation } from "../../../../libs/redux/slices/autocompSlice/autocompApiSlice";
import { useGetCurrentQuotaByClientMutation } from "../../../../libs/redux/slices/Financix/cupodispApiSlice";
import { useGetStateOperationsMutation } from "../../../../libs/redux/slices/Modimpxx/Causapay/causapayApiSlice";
import Carguedo from "../../../Carguedo/Carguedo";
import { useSaveTaskGeneralMutation } from "../../../../libs/redux/slices/Ordtraxx/Tareasxx/TareasxxApiSlice";
import { useGetSucursalesQuery } from "../../../../libs/redux/slices/Modimpxx/Backlogx/backlogxApiSlice";
import { convertCurrencyNumber } from "../utils/function";

const tipoOperacion = [
  { text: "Causaciones (Intercompañías)", value: "CAUSACION" },
  { text: "Causación y pago", value: "CAUSACION_PAGO" },
  { text: "Anticipos a proveedor", value: "ANTICIPOS_PROVEEDOR" },
  { text: "Legalización de anticipo", value: "LEGALIZACION_ANTICIPO" },
];

const processByTypes = {
  CAUSACION: 100,
  CAUSACION_PAGO: 101,
  ANTICIPOS_PROVEEDOR: 102,
  LEGALIZACION_ANTICIPO: 103,
};

const tiposConValidacionCupo = ["CAUSACION_PAGO", "ANTICIPOS_PROVEEDOR"];

//Tipos de solicitud que requieren Forma de Pago obligatoria
const tiposConFormaPago = ["CAUSACION_PAGO", "ANTICIPOS_PROVEEDOR"];

const formasDePago = [
  { text: "PSE", value: "PSE" },
  { text: "Transferencia", value: "TRANSFERENCIA" },
];

//Prioridad de gestion de la tarea. Va en TARCPRIO (ENUM de TARC{anio}) y no en
//el JSON, para que el tracking pueda ordenar por ella: ALTA, MEDIA y luego BAJA
const prioridades = [
  { text: "Alta", value: "ALTA" },
  { text: "Media", value: "MEDIA" },
  { text: "Baja", value: "BAJA" },
];

const PRIORIDAD_DEFAULT = "MEDIA";

/**
 * Fecha minima valida para la Fecha Maxima de Pago: siempre mayor a hoy
 * @returns {string} Fecha de manana en formato YYYY-MM-DD
 */
const getMinFechaMaxPago = () => {
  const manana = new Date();
  manana.setDate(manana.getDate() + 1);
  return manana.toISOString().split("T")[0];
};

//Horario límite de radicación por Forma de Pago: hora en formato "HH:mm", null = dia no aplica
const horarioFormaPago = {
  PSE: { 1: "15:29", 2: "15:29", 3: "15:29", 4: "15:29", 5: "15:29", 6: "10:45", 0: null },
  TRANSFERENCIA: { 1: "12:59", 2: "12:59", 3: "12:59", 4: "12:59", 5: "12:59", 6: null, 0: null },
};

const defaultDoRow = { DOCNROXX: "", DOCSELEC: null, DOCRESUL: [], VLRFACXX: "" };

//Plantilla de operaciones: solo aplica a Causacion y Pago, donde una misma
//factura puede ir contra varios DO (o varias facturas contra un DO) y la
//relacion la traen los usuarios en un archivo, no DO por DO
const PLANTILLA_COLUMNAS = ["DO", "NUMERO DE FACTURA", "VALOR"];
const PLANTILLA_NOMBRE = "PlantillaOperacionesGestionContable.xlsx";
const MAX_FILAS_PLANTILLA = 500;

const sucursalesPermitidas = ["BOGOTA", "CALI"];

//Opcion "Puerto" no corresponde a una sucursal real de CIMS0050: es una opcion fija que agrupa
//las tareas para notificarlas a un grupo especifico de usuarios (CLATARID 203, GESTION CONTABLE PUERTOS)
const PUERTO_SUCURSAL = { text: "PUERTOS", value: "PUERTO" };

/**
 * Normaliza el nombre de una sucursal (mayúsculas y sin tildes) para su comparación
 * @param {string} text - Nombre de la sucursal
 * @returns {string} Nombre normalizado
 */
const normalizeSucursal = (text = "") =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

/**
 * Determina si la hora actual está fuera del horario permitido para la Forma de Pago
 * @param {string} formaPago - PSE | TRANSFERENCIA
 * @returns {boolean} true si está fuera de horario
 */
const isOutOfSchedule = (formaPago) => {
  const now = new Date();
  const limite = horarioFormaPago[formaPago]?.[now.getDay()];
  if (!limite) return true;
  const [horaLimite, minLimite] = limite.split(":").map(Number);
  return now.getHours() > horaLimite || (now.getHours() === horaLimite && now.getMinutes() > minLimite);
};

//Descripcion legible del horario permitido, para mostrarla en la alerta de fuera de horario
const descripcionHorario = {
  PSE: "Lunes a viernes hasta las 3:29 p.m. y sábados hasta las 10:45 a.m. Los domingos no aplica.",
  TRANSFERENCIA: "Lunes a viernes hasta las 12:59 p.m. Sábados y domingos no aplica.",
};

/**
 * Componente para la generacion de la tarea de Gestion de Contabilidad
 * @param {Function} setGestcont - Callback para cerrar el modal
 */
const Gestcont = ({ setGestcont }) => {
  const [results, setResults] = useState([]);
  const [provider, setProvider] = useState(null);
  const [beneficiary, setBeneficiary] = useState(null);
  const [doRows, setDoRows] = useState([{ ...defaultDoRow }]);
  const [tipoOper, setTipoOper] = useState("");
  const [sucursal, setSucursal] = useState("");
  const [formaPago, setFormaPago] = useState("");
  const [prioridad, setPrioridad] = useState(PRIORIDAD_DEFAULT);
  const [hasFiles, setHasFiles] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [idArchivo, setIdArchivo] = useState(null);
  const [isUploadingSoporte, setIsUploadingSoporte] = useState(false);
  const [availableQuota, setAvailableQuota] = useState(0);
  const [valorFaltante, setValorFaltante] = useState(0);
  const [typeQuota, setTypeQuota] = useState("");
  const [erroresPlantilla, setErroresPlantilla] = useState([]);
  //DO de la solicitud que estan en un estado restringido (RQ402): mientras haya
  //alguno la solicitud completa no se puede guardar
  const [dosBloqueados, setDosBloqueados] = useState([]);
  const [isValidatingDos, setIsValidatingDos] = useState(false);
  const [estadoDosError, setEstadoDosError] = useState(false);
  const cargueRef = useRef(null);
  const validacionDosRef = useRef(0);

  const [getAutocompleteWithParams] = useGetAutocompleteWithParamsMutation();
  const { data: sucursalesData } = useGetSucursalesQuery();
  const [saveTaskGeneral, { isLoading }] = useSaveTaskGeneralMutation();
  const [getCurrentQuotaByClientId] = useGetCurrentQuotaByClientMutation();
  const [getStateOperations] = useGetStateOperationsMutation();

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      CLIIDXXX: "",
      PROIDXXX: "",
      PROID2XX: "",
      TIPOOPER: "",
      SUCIDXXX: "",
      FORMPAGX: "",
      TARCPRIO: PRIORIDAD_DEFAULT,
      FECMAXPG: "",
      VLRTOTAL: "",
      OBSANTIX: "",
    },
  });

  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    watch,
    formState: { errors },
    control,
  } = methods;

  const CLIIDXXX = useWatch({ name: "CLIIDXXX", control });
  const VLRTOTAL = useWatch({ name: "VLRTOTAL", control });
  const requiresQuotaValidation = tiposConValidacionCupo.includes(tipoOper);
  const requiresFormaPago = tiposConFormaPago.includes(tipoOper);
  //La Fecha Maxima de Pago aplica a los mismos tipos que exigen Forma de Pago:
  //Causacion y Pago y Anticipos a Proveedor
  const requiresFechaMaxPago = requiresFormaPago;
  //La plantilla de operaciones solo aplica a Causacion y Pago; conviven la
  //grilla (pocos DO) y el archivo (varias facturas o una factura para varios DO)
  const allowsPlantilla = tipoOper === "CAUSACION_PAGO";
  //Causaciones (Intercompañías): el proveedor y el beneficiario solo pueden ser
  //proveedores marcados como intercompañía
  const onlyIntercompania = tipoOper === "CAUSACION";
  const showOutOfScheduleAlert = requiresFormaPago && formaPago !== "" && isOutOfSchedule(formaPago);
  const docNumbers = [...new Set(doRows.map((r) => r.DOCNROXX).filter(Boolean))].join(",");
  const sucursalesOptions = [
    ...(sucursalesData?.data ?? []).filter((opt) => sucursalesPermitidas.includes(normalizeSucursal(opt.text))),
    PUERTO_SUCURSAL,
  ];
  const hasInsufficientQuota =
    requiresQuotaValidation && typeQuota !== "ILIMITADO" && (valorFaltante > 0 || availableQuota <= 0);
  const docsBloqueados = dosBloqueados.map((d) => d.DOCNROXX);
  //Un solo DO restringido bloquea toda la solicitud; si no se pudo validar el
  //estado tampoco se deja guardar (el servidor lo vuelve a validar igual)
  const isSolicitudBloqueada = dosBloqueados.length > 0 || isValidatingDos || estadoDosError;

  /**
   * Realiza la búsqueda de sugerencias en el autocomplete
   * @param {string} value - Texto a buscar
   * @param {Function} cb - Callback para finalizar la carga
   * @param {string} endpoint - Endpoint a consultar
   */
  const handleSearch = async (value, cb, endpoint) => {
    try {
      const response = await getAutocompleteWithParams({
        filter: value,
        endpoint,
        MODURLXX: "Modimpxx",
        PRODURLX: "Causapay",
        //En Causaciones (Intercompañías) solo se listan los proveedores marcados
        //como intercompañía en su ficha
        ...(endpoint === "getProvider" && onlyIntercompania ? { extraParams: { INTERCOM: "SI" } } : {}),
      }).unwrap();
      setResults(response.data);
    } catch (err) {
    } finally {
      cb();
    }
  };

  /**
   * Maneja el cambio de selección del proveedor o beneficiario
   * @param {Object} context - Contexto del item seleccionado
   * @param {boolean} isEmpty - Indica si se limpió la selección
   * @param {string} field - Nombre del campo (PROIDXXX | PROID2XX)
   */
  const handleChangeSelect = (context, isEmpty, field) => {
    const setters = { PROIDXXX: setProvider, PROID2XX: setBeneficiary };
    if (!isEmpty) {
      setters[field]?.(context.item.label);
      setValue(field, context.item.id);
    } else {
      setters[field]?.(null);
      setValue(field, "");
    }
  };

  /**
   * Elimina una fila de DO de la grilla
   * @param {number} index - Índice de la fila a eliminar
   */
  const removeDoRow = (index) => {
    if (doRows.length === 1) return;
    setDoRows((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Consulta el autocompletar para DOs por fila
   * @param {string} value - Texto a buscar
   * @param {Function} cb - Callback al finalizar
   * @param {number|null} index - Índice de fila del DO (null para resultados compartidos)
   */
  const operations = async (value, cb, index = null) => {
    if (watch("CLIIDXXX") !== "") {
      const filter = { CLIIDXXX: watch("CLIIDXXX").split(" - ")[0], DOCNROXX: value };
      try {
        //Endpoint propio de Gestion Contable: a diferencia de getDoByClient
        //(compartido con otras pantallas) trae tambien los DO restringidos,
        //marcados, para que el usuario vea por que no puede usarlos
        const response = await getAutocompleteWithParams({
          filter,
          endpoint: "getDoByClientState",
          MODURLXX: "Modimpxx",
          PRODURLX: "Causapay",
        }).unwrap();
        if (index !== null) {
          setDoRows((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], DOCRESUL: response.data };
            return updated;
          });
        } else {
          setResults(response.data);
        }
      } catch (err) {
      } finally {
        cb();
      }
    } else {
      shootToast({ text: "Debe seleccionar un Importador", variant: "info", autoHide: true, duration: 2000 });
      cb();
    }
  };

  /**
   * Maneja la selección de un DO en la grilla
   * @param {Object} context - Contexto del item seleccionado
   * @param {boolean} isEmpty - Indica si se limpió la selección
   * @param {number} index - Índice de la fila
   */
  const handleDoSelect = (context, isEmpty, index) => {
    setDoRows((prev) => {
      const updated = [...prev];
      if (!isEmpty) {
        if (context.item.BLOQUEAX) {
          shootToast({ variant: "error", text: context.item.MENSAJEX, autoHide: false });
        }
        updated[index] = { ...updated[index], DOCNROXX: context.item.id, DOCSELEC: context.item.label };
        setValue(`DOS.${index}.DOCNROXX`, context.item.id);
      } else {
        updated[index] = { ...updated[index], DOCNROXX: "", DOCSELEC: null };
        setValue(`DOS.${index}.DOCNROXX`, "");
      }
      return updated;
    });
  };

  /**
   * Consulta el cupo disponible del cliente para las operaciones seleccionadas
   * @param {string} clientId - Id del cliente
   * @param {string} docs - Números de DO separados por coma
   */
  const handleGetQuotaClient = useCallback(
    async (clientId, docs = "") => {
      await getCurrentQuotaByClientId({ filters: { CLIIDXXX: clientId, DOCNROXX: docs } })
        .unwrap()
        .then((response) => {
          setAvailableQuota(response.data.CUPODISP);
          setTypeQuota(response.data.CLITICUP);
        })
        .catch(() => {
          setAvailableQuota(0);
          setTypeQuota("");
        });
    },
    [getCurrentQuotaByClientId],
  );

  /**
   * Descarga la plantilla vacía de operaciones (DO, número de factura y valor)
   */
  const handleDownloadPlantilla = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("OPERACIONES");
    sheet.columns = PLANTILLA_COLUMNAS.map((header) => ({ header, key: header, width: 24 }));
    PLANTILLA_COLUMNAS.forEach((_, index) => {
      const cell = sheet.getCell(1, index + 1);
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "bacfe1" } };
      cell.font = { bold: true };
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = PLANTILLA_NOMBRE;
    link.click();
    window.URL.revokeObjectURL(link.href);
  };

  /**
   * Convierte el valor de una celda de la plantilla a número
   * @param {*} celda - Valor crudo de la celda
   * @returns {number} Valor numérico (0 si no se puede interpretar)
   */
  const parseValorCelda = (celda) => {
    if (typeof celda === "number") return celda;
    const texto = String(celda ?? "").replace(/[$\s.]/g, "").replace(",", ".");
    const numero = Number(texto);
    return Number.isFinite(numero) ? numero : 0;
  };

  /**
   * Lee la plantilla diligenciada y reemplaza las filas de operaciones
   * @param {File} archivo - Archivo xlsx seleccionado por el usuario
   */
  const handleUploadPlantilla = async (archivo) => {
    if (!archivo) return;
    setErroresPlantilla([]);
    try {
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await archivo.arrayBuffer());
      const sheet = workbook.worksheets[0];
      if (!sheet) {
        shootToast({ variant: "error", text: "El archivo no tiene ninguna hoja", autoHide: true });
        return;
      }
      const filas = [];
      const errores = [];
      sheet.eachRow((row, numero) => {
        if (numero === 1 || filas.length >= MAX_FILAS_PLANTILLA) return;
        const documento = String(row.getCell(1).value ?? "").trim();
        const factura = String(row.getCell(2).value ?? "").trim();
        const valor = parseValorCelda(row.getCell(3).value);
        if (documento === "" && factura === "" && valor === 0) return;
        if (documento === "" || factura === "" || valor <= 0) {
          errores.push(`Fila ${numero}: DO, número de factura y valor son obligatorios`);
          return;
        }
        filas.push({ DOCNROXX: documento, DOCSELEC: documento, DOCRESUL: [], CNSCOM2X: factura, VLRFACXX: valor });
      });
      if (filas.length === 0) {
        setErroresPlantilla(errores);
        shootToast({ variant: "error", text: "El archivo no trae ninguna fila válida", autoHide: false });
        return;
      }
      //Las filas del archivo reemplazan lo que haya en la grilla, para que no
      //queden las dos fuentes mezcladas y el usuario revise antes de guardar
      setDoRows(filas);
      filas.forEach((fila, index) => {
        setValue(`DOS.${index}.DOCNROXX`, fila.DOCNROXX);
        setValue(`DOS.${index}.CNSCOM2X`, fila.CNSCOM2X);
        setValue(`DOS.${index}.VLRFACXX`, fila.VLRFACXX);
      });
      const total = filas.reduce((suma, fila) => suma + fila.VLRFACXX, 0);
      setValue("VLRTOTAL", String(total));
      setErroresPlantilla(errores);
      shootToast({
        variant: errores.length > 0 ? "warning" : "success",
        text:
          errores.length > 0
            ? `Se cargaron ${filas.length} operación(es); ${errores.length} fila(s) quedaron por fuera`
            : `Se cargaron ${filas.length} operación(es) por $ ${convertCurrencyNumber(total)}`,
        autoHide: true,
        duration: 5000,
      });
    } catch (error) {
      shootToast({ variant: "error", text: "No se pudo leer el archivo: verifique que sea la plantilla", autoHide: false });
    }
  };

  /**
   * Maneja el envío del formulario
   * @param {Object} formData - Datos del formulario
   */
  const onSubmit = async (formData) => {
    if (!idArchivo) {
      shootToast({ variant: "error", text: "El cargue de soporte es obligatorio para continuar", autoHide: false });
      return;
    }
    const validDos = (formData.DOS ?? []).filter((r) => r.DOCNROXX !== "");
    if (validDos.length === 0) {
      shootToast({ variant: "error", text: "Debe agregar al menos un DO", autoHide: false });
      return;
    }
    if (isSolicitudBloqueada) {
      shootToast({
        variant: "error",
        text: dosBloqueados.length > 0 ? dosBloqueados.map((d) => d.MENSAJEX).join(" ") : "No se pudo validar el estado de los DO",
        autoHide: false,
      });
      return;
    }
    if (hasInsufficientQuota) {
      shootToast({ variant: "error", text: "La operación no cuenta con cupo disponible suficiente", autoHide: false });
      return;
    }
    if (requiresFormaPago && !formData.FORMPAGX) {
      shootToast({ variant: "error", text: "El campo Forma de Pago no puede estar vacío", autoHide: false });
      return;
    }
    if (requiresFechaMaxPago && (!formData.FECMAXPG || formData.FECMAXPG <= new Date().toISOString().split("T")[0])) {
      shootToast({
        variant: "error",
        text: "La Fecha Máxima de Pago es obligatoria y debe ser posterior a hoy",
        autoHide: false,
      });
      return;
    }
    setIsCreating(true);
    try {
      const sucursalText = sucursalesOptions.find((opt) => opt.value === formData.SUCIDXXX)?.text ?? "";
      const sucursalNorm = normalizeSucursal(sucursalText);
      const clatarid =
        formData.SUCIDXXX === PUERTO_SUCURSAL.value ? 203 : sucursalNorm === "CALI" ? 202 : 201;
      if (requiresFormaPago && isOutOfSchedule(formData.FORMPAGX)) {
        shootToast({
          variant: "warning",
          text: `TAREA FUERA DE HORARIO. El horario establecido para ${formData.FORMPAGX} es: ${descripcionHorario[formData.FORMPAGX]} Esta tarea se gestionará en el próximo turno.`,
          autoHide: true,
          duration: 8000,
        });
      }
      const dataSolved = {
        CLIIDXXX: formData.CLIIDXXX.split(" - ")[0],
        MODIDXXX: 129,
        PROIDXXX: processByTypes[formData.TIPOOPER],
        CLATARID: clatarid,
        TOKIDXXX: 100,
        TARCPRIO: formData.TARCPRIO,
        TARDICXX: {
          //Cada operacion viaja con su factura y su valor: esa es la relacion que
          //antes llegaba por fuera en un Excel y no quedaba en la tarea
          JSONOPER: validDos.map((r) => ({
            DOCNROXX: r.DOCNROXX,
            CNSCOM2X: r.CNSCOM2X ?? "",
            VLRFACXX: r.VLRFACXX ?? "",
          })),
          VLRTOTAL: formData.VLRTOTAL,
          PROIDXXX: formData.PROIDXXX,
          PROID2XX: formData.PROID2XX,
          TIPOOPER: formData.TIPOOPER,
          SUCIDXXX: formData.SUCIDXXX,
          FORMPAGX: formData.FORMPAGX,
          //Fecha maxima de pago: viaja en el JSON de la tarea y se hereda a la
          //tarea de pago y a la de tesoreria, que se crean a partir de esta
          FECMAXPG: formData.FECMAXPG,
          //Deja registrado que la tarea se radico fuera del horario de su forma de pago.
          //La marca en el tracking se calcula con este dato + la fecha de creacion.
          FUERAHOR: requiresFormaPago && isOutOfSchedule(formData.FORMPAGX) ? "SI" : "NO",
          OBSANTIX: formData.OBSANTIX,
        },
        ESTAIDXX: 100,
        ESTGESID: 100,
        IDARCHXX: idArchivo,
      };
      await saveTaskGeneral(dataSolved)
        .unwrap()
        .then((response) => {
          shootToast({
            variant: "success",
            text: `Se creo la Tarea ${response?.data?.TARIDXXX}`,
            autoHide: true,
            duration: 3000,
          });
          setGestcont(false);
        })
        .catch((error) => {
          //El servidor rechaza la solicitud si algun DO quedo en estado restringido
          //(RQ402); el modal se queda abierto para que el usuario lo vea y corrija
          const mensajes = Object.values(error?.data?.errors ?? {}).flat().filter((m) => typeof m === "string");
          shootToast({
            variant: "error",
            text: mensajes.length > 0 ? mensajes.join(" ") : "No se pudo crear la tarea",
            autoHide: false,
          });
        });
    } catch (e) {
    } finally {
      setIsCreating(false);
    }
  };

  const isFormValid = !Object.keys(errors).length;

  // Consulta el cupo del cliente cuando aplica validación y hay DOs seleccionados
  useEffect(() => {
    if (requiresQuotaValidation && CLIIDXXX !== "" && docNumbers.length > 0) {
      handleGetQuotaClient(CLIIDXXX.split(" - ")[0], docNumbers);
    }
  }, [requiresQuotaValidation, CLIIDXXX, docNumbers, handleGetQuotaClient]);

  // Valida el estado de todos los DO de la solicitud (grilla y plantilla) cada vez
  // que cambian los DO o el cliente. Solo cuenta la respuesta de la ultima consulta
  useEffect(() => {
    const consulta = ++validacionDosRef.current;
    if (CLIIDXXX === "" || docNumbers.length === 0) {
      setDosBloqueados([]);
      setIsValidatingDos(false);
      setEstadoDosError(false);
      return;
    }
    setIsValidatingDos(true);
    getStateOperations({ CLIIDXXX: CLIIDXXX.split(" - ")[0], DOCNROXX: docNumbers.split(",") })
      .unwrap()
      .then((response) => {
        if (consulta !== validacionDosRef.current) return;
        setDosBloqueados((response?.data ?? []).filter((d) => d.BLOQUEAX));
        setEstadoDosError(false);
      })
      .catch(() => {
        if (consulta !== validacionDosRef.current) return;
        setDosBloqueados([]);
        setEstadoDosError(true);
      })
      .finally(() => {
        if (consulta === validacionDosRef.current) setIsValidatingDos(false);
      });
  }, [CLIIDXXX, docNumbers, getStateOperations]);

  // Recalcula el valor faltante cuando cambia el valor de la solicitud o el cupo disponible
  useEffect(() => {
    const total = Number(VLRTOTAL) || 0;
    const calculated = total - availableQuota;
    setValorFaltante(calculated < 0 ? 0 : calculated);
  }, [availableQuota, VLRTOTAL]);

  return (
    <Modal size="xl" isOpen>
      <ModalHeader>
        <Flexbox justifyContent="between" alignItems="center">
          <Heading variant="heading-6" color="accent-600">
            Solicitud Gestión Contabilidad
          </Heading>
          <Button
            variant="secondary"
            icon="close"
            onlyIcon
            size="m"
            disabled={isUploadingSoporte}
            onClick={() => setGestcont(false)}
          />
        </Flexbox>
      </ModalHeader>
      <FormProvider {...methods}>
        <form id="gestcont" onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Box spacingX={2} spacingTop={2} spacingBottom={4} rounded="md" bg="white">
              <Stack columnsVariant="form-2" gap={4}>
                <StackRow>
                  <Label>Tipo de Solicitud</Label>
                  <Select
                    options={tipoOperacion}
                    {...register("TIPOOPER", { required: true })}
                    value={tipoOper}
                    onChangeValue={(context) => {
                      setTipoOper(context.option.value);
                      setValue("TIPOOPER", context.option.value);
                      clearErrors("TIPOOPER");
                      if (!tiposConFormaPago.includes(context.option.value)) {
                        setFormaPago("");
                        setValue("FORMPAGX", "");
                        setValue("FECMAXPG", "");
                      }
                      //El universo de proveedores depende del tipo (en
                      //Intercompañías es solo un subconjunto), asi que lo ya
                      //elegido se limpia para no dejar uno que no aplica
                      setProvider(null);
                      setBeneficiary(null);
                      setValue("PROIDXXX", "");
                      setValue("PROID2XX", "");
                      setResults([]);
                    }}
                    isInvalid={errors.TIPOOPER}
                  />
                  {errors.TIPOOPER && <FieldError>El campo Tipo de operación no puede estar vacío</FieldError>}
                </StackRow>
                <StackRow>
                  <Label>Cliente</Label>
                  <Controller
                    control={control}
                    name="CLIIDXXX"
                    rules={{ required: true }}
                    render={({ field: { onChange, value, ref } }) => (
                      <Autocomplete
                        ref={ref}
                        placeholder="Buscar cliente"
                        textLoading="Buscando..."
                        textNoResults="No se encontraron registros"
                        minLengthToSearch={2}
                        selected={value}
                        searchResults={results}
                        onChangeSearch={(val, cb) => handleSearch(val, cb, "getClientById")}
                        onItemClick={(context) => onChange(context.item.label)}
                        onResetSelected={() => onChange("")}
                        isInvalid={errors.CLIIDXXX}
                      />
                    )}
                  />
                  {errors.CLIIDXXX && <FieldError>El campo Cliente no puede estar vacío</FieldError>}
                </StackRow>
                <StackRow>
                  <Label>Id o Nombre del Proveedor</Label>
                  <Autocomplete
                    {...register("PROIDXXX", { required: true })}
                    placeholder="Id o Nombre del Proveedor"
                    textLoading="Buscando..."
                    textNoResults="No se encontraron registros"
                    minLengthToSearch={2}
                    selected={provider}
                    searchResults={results}
                    onChangeSearch={(val, cb) => handleSearch(val, cb, "getProvider")}
                    onItemClick={(context) => {
                      handleChangeSelect(context, false, "PROIDXXX");
                      clearErrors("PROIDXXX");
                    }}
                    onResetSelected={(context) => handleChangeSelect(context, true, "PROIDXXX")}
                    isInvalid={errors.PROIDXXX}
                  />
                  {errors.PROIDXXX && <FieldError>El campo Proveedor no puede estar vacío</FieldError>}
                </StackRow>
                <StackRow>
                  <Label>Id o Nombre del Beneficiario</Label>
                  <Autocomplete
                    {...register("PROID2XX")}
                    placeholder="Id o Nombre del Beneficiario"
                    textLoading="Buscando..."
                    textNoResults="No se encontraron registros"
                    minLengthToSearch={2}
                    selected={beneficiary}
                    searchResults={results}
                    onChangeSearch={(val, cb) => handleSearch(val, cb, "getProvider")}
                    onItemClick={(context) => {
                      handleChangeSelect(context, false, "PROID2XX");
                      clearErrors("PROID2XX");
                    }}
                    onResetSelected={(context) => handleChangeSelect(context, true, "PROID2XX")}
                  />
                </StackRow>
                <StackRow>
                  <Label>Sucursal</Label>
                  <Select
                    options={sucursalesOptions}
                    {...register("SUCIDXXX", { required: true })}
                    value={sucursal}
                    onChangeValue={(context) => {
                      setSucursal(context.option.value);
                      setValue("SUCIDXXX", context.option.value);
                      clearErrors("SUCIDXXX");
                    }}
                    isInvalid={errors.SUCIDXXX}
                  />
                  {errors.SUCIDXXX && <FieldError>El campo Sucursal no puede estar vacío</FieldError>}
                </StackRow>
                <StackRow>
                  <Label>Prioridad</Label>
                  <Select
                    options={prioridades}
                    {...register("TARCPRIO", { required: true })}
                    value={prioridad}
                    onChangeValue={(context) => {
                      setPrioridad(context.option.value);
                      setValue("TARCPRIO", context.option.value);
                      clearErrors("TARCPRIO");
                    }}
                    isInvalid={errors.TARCPRIO}
                  />
                  {errors.TARCPRIO && <FieldError>El campo Prioridad no puede estar vacío</FieldError>}
                </StackRow>
                {requiresFormaPago && (
                  <StackRow>
                    <Label>Forma de Pago</Label>
                    <Select
                      options={formasDePago}
                      {...register("FORMPAGX", { required: requiresFormaPago })}
                      value={formaPago}
                      onChangeValue={(context) => {
                        setFormaPago(context.option.value);
                        setValue("FORMPAGX", context.option.value);
                        clearErrors("FORMPAGX");
                      }}
                      isInvalid={errors.FORMPAGX}
                    />
                    {errors.FORMPAGX && <FieldError>El campo Forma de Pago no puede estar vacío</FieldError>}
                  </StackRow>
                )}
                {requiresFechaMaxPago && (
                  <StackRow>
                    <Label>Fecha Máxima de Pago</Label>
                    <Controller
                      name="FECMAXPG"
                      control={control}
                      rules={{
                        required: requiresFechaMaxPago,
                        validate: (v) =>
                          !requiresFechaMaxPago ||
                          (!!v && v > new Date().toISOString().split("T")[0]) ||
                          "La Fecha Máxima de Pago debe ser posterior a hoy",
                      }}
                      render={({ field }) => (
                        <Input {...field} type="date" min={getMinFechaMaxPago()} isInvalid={!!errors.FECMAXPG} />
                      )}
                    />
                    {errors.FECMAXPG && (
                      <FieldError>
                        {errors.FECMAXPG.message ?? "El campo Fecha Máxima de Pago no puede estar vacío"}
                      </FieldError>
                    )}
                  </StackRow>
                )}
                <StackRow>
                  <Label>Valor</Label>
                  <Controller
                    name="VLRTOTAL"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="currency"
                        currencyMode="european"
                        placeholder="Valor"
                        isInvalid={!!errors.VLRTOTAL}
                      />
                    )}
                  />
                  {errors.VLRTOTAL && <FieldError>El campo Valor no puede estar vacío</FieldError>}
                </StackRow>
              </Stack>
            </Box>
            {showOutOfScheduleAlert && (
              <Box bg="warning-500" rounded="md" spacing={5} mb={4}>
                <Flexbox justifyContent="center" alignItems="center" gap={3}>
                  <Heading variant="heading-5" color="white" alignment="center">
                    ⚠ TAREA FUERA DE HORARIO
                  </Heading>
                </Flexbox>
                <Box mt={3}>
                  <Label color="white" alignment="center">
                    El horario establecido para {formaPago === "PSE" ? "PSE" : "TRANSFERENCIA"} es:{" "}
                    {descripcionHorario[formaPago]}
                  </Label>
                </Box>
                <Box mt={2}>
                  <Label color="white" alignment="center">
                    Esta tarea se creará fuera de horario y se gestionará en el próximo turno.
                  </Label>
                </Box>
              </Box>
            )}
            <Box mb={4}>
              <Divider text="Operaciones" color="accent-600" />
            </Box>
            {allowsPlantilla && (
              <Box bg="neutral-100" border={1} borderColor="neutral-300" rounded="md" spacing={3} mb={4}>
                <Description variant="description-4" color="neutral-600">
                  Si la relación viene en archivo (varias facturas, o una misma factura para varios DO), descargue la
                  plantilla, diligénciela con DO, número de factura y valor, y cárguela: sus filas reemplazan la grilla y
                  el Valor de la solicitud se recalcula con la suma.
                </Description>
                <Box mt={3}>
                  <Flexbox justifyContent="start" alignItems="center" gap={3}>
                    <Button
                      type="button"
                      variant="secondary-light"
                      icon="download"
                      size="m"
                      onClick={handleDownloadPlantilla}
                    >
                      Plantilla
                    </Button>
                    <Button
                      type="button"
                      variant="secondary-light"
                      icon="upload"
                      size="m"
                      onClick={() => cargueRef.current?.click()}
                    >
                      Cargar Plantilla
                    </Button>
                    <input
                      ref={cargueRef}
                      type="file"
                      accept=".xlsx"
                      style={{ display: "none" }}
                      onChange={(event) => {
                        handleUploadPlantilla(event.target.files?.[0]);
                        event.target.value = "";
                      }}
                    />
                  </Flexbox>
                </Box>
                {erroresPlantilla.length > 0 && (
                  <Box mt={3} bg="warning-500" rounded="md" spacing={3}>
                    <Label color="white">Filas que quedaron por fuera del cargue:</Label>
                    {erroresPlantilla.slice(0, 10).map((error) => (
                      <Label key={error} color="white">
                        {error}
                      </Label>
                    ))}
                    {erroresPlantilla.length > 10 && (
                      <Label color="white">y {erroresPlantilla.length - 10} más...</Label>
                    )}
                  </Box>
                )}
              </Box>
            )}
            <Box bg="neutral-200" borderColor="neutral-300" rounded="md" spacing={3} mb={4}>
              <Stack gap={2}>
                {doRows.map((row, index) => (
                  <Box key={index} spacingX={2} spacingTop={2} spacingBottom={4} rounded="md" bg="white">
                    <Stack gap={7} responsiveColumns={{ small: 4 }}>
                      <StackRow
                        responsiveColSpan={{
                          initial: allowsPlantilla ? 1 : tipoOper !== "ANTICIPOS_PROVEEDOR" ? 2 : 3,
                        }}
                      >
                        <Label>Número de DO</Label>
                        <Autocomplete
                          {...register(`DOS.${index}.DOCNROXX`, { required: true })}
                          placeholder="Número de DO"
                          textLoading="Buscando..."
                          textNoResults="No se encontraron registros"
                          minLengthToSearch={2}
                          selected={row.DOCSELEC}
                          searchResults={row.DOCRESUL}
                          onChangeSearch={(val, cb) => operations(val, cb, index)}
                          onItemClick={(context) => {
                            handleDoSelect(context, false, index);
                            clearErrors(`DOS.${index}.DOCNROXX`);
                          }}
                          onResetSelected={(context) => handleDoSelect(context, true, index)}
                          isInvalid={errors.DOS?.[index]?.DOCNROXX || docsBloqueados.includes(row.DOCNROXX)}
                        />
                        {errors.DOS?.[index]?.DOCNROXX && <FieldError>El campo DO no puede estar vacío</FieldError>}
                      </StackRow>
                      {tipoOper !== "ANTICIPOS_PROVEEDOR" && (
                        <StackRow>
                          <Label>Número de Factura</Label>
                          <Input {...register(`DOS.${index}.CNSCOM2X`, { required: true })} />
                          {errors.DOS?.[index]?.CNSCOM2X && (
                            <FieldError>El campo Número de Factura no puede estar vacío</FieldError>
                          )}
                        </StackRow>
                      )}
                      {allowsPlantilla && (
                        <StackRow>
                          <Label>Valor</Label>
                          <Controller
                            name={`DOS.${index}.VLRFACXX`}
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                type="currency"
                                currencyMode="european"
                                placeholder="Valor"
                                isInvalid={!!errors.DOS?.[index]?.VLRFACXX}
                                onChange={(event) => {
                                  field.onChange(event);
                                  setDoRows((prev) => {
                                    const updated = [...prev];
                                    updated[index] = { ...updated[index], VLRFACXX: event.target.value };
                                    return updated;
                                  });
                                }}
                              />
                            )}
                          />
                          {errors.DOS?.[index]?.VLRFACXX && <FieldError>El campo Valor no puede estar vacío</FieldError>}
                        </StackRow>
                      )}
                      <StackRow>
                        <Box spacingY={6}>
                          <Button
                            type="button"
                            onClick={() => removeDoRow(index)}
                            variant="error-light"
                            icon="trash"
                            disabled={doRows.length === 1}
                            size="m"
                            onlyIcon
                          />
                        </Box>
                      </StackRow>
                    </Stack>
                  </Box>
                ))}
                <StackRow>
                  <Button
                    type="button"
                    variant="secondary-light"
                    icon="plus"
                    size="l"
                    onClick={() => setDoRows((prev) => [...prev, { ...defaultDoRow }])}
                  >
                    Agregar DO
                  </Button>
                </StackRow>
              </Stack>
            </Box>
            {(dosBloqueados.length > 0 || estadoDosError) && (
              <Box bg="error-500" rounded="md" spacing={5} mb={4}>
                <Heading variant="heading-5" color="white" alignment="center">
                  ⛔ SOLICITUD BLOQUEADA
                </Heading>
                {dosBloqueados.map((d) => (
                  <Box mt={2} key={d.DOCNROXX}>
                    <Label color="white">{d.MENSAJEX}</Label>
                  </Box>
                ))}
                <Box mt={3}>
                  <Label color="white" alignment="center">
                    {estadoDosError
                      ? "No se pudo validar el estado de los DO. Intente de nuevo; la solicitud no se puede guardar sin esa validación."
                      : "Retire estos DO para poder guardar la solicitud."}
                  </Label>
                </Box>
              </Box>
            )}
            {requiresQuotaValidation && CLIIDXXX !== "" && docNumbers.length > 0 && (
              <Box spacing={2} bg="success-300" rounded="md" mb={4}>
                <Stack columnsVariant="form-2" gap={3}>
                  <StackRow>
                    <Label pt={5} m={3} color="black">
                      CUPO DISPONIBLE
                    </Label>
                  </StackRow>
                  <StackRow>
                    <Label alignment="right">
                      {typeQuota !== "ILIMITADO" ? `$ ${convertCurrencyNumber(availableQuota)}` : typeQuota}
                    </Label>
                  </StackRow>
                </Stack>
                {hasInsufficientQuota && (
                  <Box mt={3} bg="error-400" rounded="md" spacing={3}>
                    <Label color="white" alignment="center">
                      {availableQuota <= 0
                        ? "La operación no cuenta con Cupo disponible"
                        : "La operación no cuenta con cupo suficiente"}
                    </Label>
                  </Box>
                )}
              </Box>
            )}
            <Box mb={4} mt={4}>
              <Divider text="Soporte" color="accent-600" />
            </Box>
            <Stack columnsVariant="form-1" gap={3}>
              <StackRow>
                <Box spacing={5}>
                  <Carguedo
                    genioParams={{
                      EMPNITXX: "800251957",
                      AREAIDXX: "126",
                      SERIEIDX: "195",
                      TIPOIDXX: "760",
                      NEWSALDO: "",
                      PARENTSX: [],
                    }}
                    jsonMetx={{ CLIIDXXX: watch("CLIIDXXX").split(" - ")[0] }}
                    sendOnDrop={true}
                    onFilesChange={(files) => setHasFiles(files.length > 0)}
                    onUploadingChange={setIsUploadingSoporte}
                    onUploadSuccess={(uploadResults) => {
                      const success = uploadResults?.find((r) => r.status === "success");
                      if (success) {
                        setIdArchivo(success.IDARCHXX);
                      }
                    }}
                  />
                </Box>
              </StackRow>
            </Stack>
            <Stack gap={3}>
              <StackRow>
                <Label>Observaciones</Label>
                <Description variant="description-4">
                  Incluir información adicional relevante para la gestión de contabilidad.
                </Description>
                <Textarea {...register("OBSANTIX")} variant="white" />
              </StackRow>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Flexbox justifyContent="between" alignItems="center" gap={4}>
              <Button
                type="button"
                variant="secondary"
                size="2l"
                minWidth={200}
                disabled={isUploadingSoporte}
                onClick={() => setGestcont(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary-bold"
                size="2l"
                minWidth={200}
                disabled={!isFormValid || hasInsufficientQuota || isUploadingSoporte || isSolicitudBloqueada}
                isLoading={isCreating}
              >
                Guardar
              </Button>
            </Flexbox>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default Gestcont;
